<template>
  <div
    :id="pageId"
    class="page-content"
    :class="pageId"
  >
    <div class="steps-container">
      <el-steps
        :active="activeStep"
        finish-status="success"
        align-center
      >
        <el-step
          title="Step 1"
          description="Customer Information"
        />
        <el-step
          title="Step 2"
          description="Family Member"
        />
        <el-step
          title="Step 3"
          description="Billing Information"
        />
        <el-step
          title="Step 4"
          description="Done"
        />
      </el-steps>
    </div>

    <create-update-customer
      v-if="activeStep === PartnerCustomerRegistrationSteps.CustomerInformation"
      :partner-code="partnerCode"
      @goto-next-step="nextStep"
    />

    <family-member v-if="activeStep === PartnerCustomerRegistrationSteps.FamilyMember" />

    <payment
      v-if="activeStep === PartnerCustomerRegistrationSteps.Payment"
      :partner-code="partnerCode"
      @goto-next-step="nextStep"
    />

    <confirm-screen
      v-if="activeStep === PartnerCustomerRegistrationSteps.Final"
      :partner-code="partnerCode"
      @init-registration-data="initData"
    />

    <div class="form-submit-button-group mb-20">
      <p-button
        v-if="isBackStepAvailable"
        id="btnBack"
        type="warning"
        round
        @click="backStep"
      >
        {{ `Back To Step ${activeStep}` }}
      </p-button>

      <p-button
        v-if="isNextStepAvailable"
        id="btnNext"
        type="purple"
        round
        @click="nextStep"
      >
        {{ activeStep === PartnerCustomerRegistrationSteps.Payment ? 'Complete & Activate GOGO' : 'Go to Next Step' }}
      </p-button>
    </div>
  </div>
</template>

<script lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';

import util from '@/Utils/util';
import { usePartner } from '@/use/partner';
import { useCustomer } from '@/use/customer';
import { PartnerCustomerRegistrationSteps } from '@/models/partner';

import CreateUpdateCustomer from '@/components/Main/Operator/Registration/CreateUpdateCustomer.vue';
import FamilyMember from '@/components/Main/Operator/Registration/FamilyMember.vue';
import Payment from '@/components/Main/Operator/Registration/Payment.vue';
import ConfirmScreen from '@/components/Main/Operator/Registration/ConfirmScreen.vue';

const pageId = 'partner-registration-user';

export default {
  name: pageId,

  components: {
    CreateUpdateCustomer,
    FamilyMember,
    Payment,
    ConfirmScreen,
  },

  setup: () => {
    const { partnerCode } = usePartner();
    const { newCustomerInfo, setNewCustomerInfo } = useCustomer();

    const activeStep = ref(PartnerCustomerRegistrationSteps.CustomerInformation);

    const isNextStepAvailable = computed(() => {
      if (activeStep.value === PartnerCustomerRegistrationSteps.CustomerInformation && newCustomerInfo.value?.id) {
        return true;
      }

      if (activeStep.value === PartnerCustomerRegistrationSteps.FamilyMember) {
        return true;
      }

      if (activeStep.value === PartnerCustomerRegistrationSteps.Payment) {
        return true;
      }

      return false;
    });

    const isBackStepAvailable = computed(() => {
      if (activeStep.value === PartnerCustomerRegistrationSteps.CustomerInformation) {
        return false;
      }

      if (activeStep.value === PartnerCustomerRegistrationSteps.Final) {
        return false;
      }

      return true;
    });

    const initData = () => {
      setNewCustomerInfo({
        phoneNumber: '',
      });

      activeStep.value = PartnerCustomerRegistrationSteps.CustomerInformation;
    };

    const backStep = () => {
      activeStep.value--;
    };
    const nextStep = () => {
      activeStep.value++;
    };

    onMounted(() => {
      util.moveToTop();
    });

    onBeforeUnmount(() => {
      if (activeStep.value === PartnerCustomerRegistrationSteps.Final) {
        initData();
      }
    });

    return {
      util,
      pageId,
      PartnerCustomerRegistrationSteps,

      isBackStepAvailable,
      isNextStepAvailable,
      activeStep,
      newCustomerInfo,
      partnerCode,

      backStep,
      nextStep,
      initData,
    };
  },
};
</script>
